import { v4 } from "uuid";

import { EmbeddedNode } from "@intelligems/ig-types";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { initDraft } from "@src/store/slices/experienceBuilder/basicMod/thunks/initDraft";
import { getModificationsLockStatus } from "@src/store/slices/experienceBuilder/common/selectors/getModificationsLockStatus";
import { getVariationMods } from "@src/store/slices/experienceBuilder/common/selectors/getVariationMods";
import { cancelDraft } from "@src/store/slices/experienceBuilder/common/thunks/cancelDraft";
import { trackElementSelected } from "@src/store/slices/experienceBuilder/common/thunks/mixPanel";
import { getSummaryTextFromHTML } from "@src/store/slices/experienceBuilder/common/utils/html/getSummaryTextFromHTML";
import { getTextFromHTML } from "@src/store/slices/experienceBuilder/common/utils/html/getTextFromHTML";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { BasicMod } from "@src/store/slices/experienceBuilder/types";

const { getVariationId, getLocation } = experienceBuilderSlice.selectors;
const { setEmbeddedNodes } = experienceBuilderSlice.actions;

export const selectElement = createAsyncThunk<
  void,
  { node: EmbeddedNode; nodes: EmbeddedNode[] }
>(
  "experienceBuilder/selectElement",
  async ({ node, nodes }, { dispatch, getState }) => {
    const { selector, html } = node;

    const lockStatus = getModificationsLockStatus(getState());
    if (lockStatus === "locked") {
      return;
    }

    const confirmed = await dispatch(cancelDraft()).unwrap();
    if (!confirmed) {
      return;
    }

    // Fallback for BasicMods the plugin couldn't resolve via onsiteEditId — e.g.
    // multi-element selectors (only elements[0] is cached), regex mods, etc.
    const mods = getVariationMods(getState());
    const mod = mods.find(
      (m): m is BasicMod =>
        m.category === "basicMod" && "selector" in m && m.selector === selector
    );
    if (mod) {
      void dispatch(initDraft(mod));
      return;
    }

    void dispatch(
      trackElementSelected({
        selector,
        elementType: node.type,
        originalText:
          node.type === "text" ? getSummaryTextFromHTML(html) : undefined,
      })
    );

    const variationId = getVariationId(getState());
    const url = getLocation(getState()) ?? undefined;
    const id = v4();

    switch (node.type) {
      case "image": {
        void dispatch(
          initDraft({
            type: "image",
            category: "basicMod",
            id,
            groupId: selector,
            variationId,
            selector,
            remove: false,
            src: "",
            html,
            editMode: "image",
            imageSearchUrl: null,
            nodes,
            createdAtUrl: url,
          })
        );
        break;
      }
      case "text": {
        const text = getTextFromHTML(html);
        void dispatch(
          initDraft({
            type: "text",
            category: "basicMod",
            id,
            groupId: selector,
            variationId,
            selector,
            find: "",
            replace: text,
            html,
            remove: false,
            editMode: "text",
            insertType: "replace",
            nodes,
            createdAtUrl: url,
          })
        );
        break;
      }
      case "layout": {
        void dispatch(
          initDraft({
            type: "layout",
            category: "basicMod",
            id,
            groupId: selector,
            variationId,
            selector,
            html,
            remove: false,
            insertType: "replace",
            nodes,
            createdAtUrl: url,
          })
        );
        break;
      }
    }

    dispatch(setEmbeddedNodes({ [selector]: node }));
  }
);
